import { noteFragment } from "@mn/shared";
import { memo, useCallback, useRef } from "react";
import type * as Y from "yjs";
import { useBoard } from "../BoardContext";
import { cardColors } from "../colors";
import { useCard } from "../hooks";
import { useBoardUI, useIsSelected } from "../store";
import { useElementHeight } from "../useResizeObserver";
import { BoardTileBody } from "./BoardCard";
import { CardChips } from "./CardChips";
import { ColumnBody } from "./ColumnCard";
import { CommentBody } from "./CommentCard";
import { DocumentBody } from "./DocumentCard";
import { FileBody, MediaBody } from "./FileCard";
import { HeadingBody } from "./HeadingCard";
import { ImageBody } from "./ImageCard";
import { LinkBody } from "./LinkCard";
import { NoteBody } from "./NoteCard";
import { SketchBody } from "./SketchCard";
import { SwatchBody } from "./SwatchCard";
import { TableBody } from "./TableCard";
import { TodoBody } from "./TodoCard";

interface Props {
  id: string;
  ymap: Y.Map<unknown>;
  /** Rendered inside a column: no absolute position, full width, height reported to the column. */
  inColumn?: boolean;
  onHeight?: (id: string, h: number) => void;
}

/** Positions a card in page space (or inside a column) and routes pointer events to the interaction engine. */
export const CardView = memo(function CardView({ id, ymap, inColumn, onHeight }: Props) {
  const c = useCard(ymap);
  const { interactions, index, canEdit } = useBoard();
  const selected = useIsSelected(id);
  const editing = useBoardUI((s) => s.editingId === id);
  const drag = useBoardUI((s) => s.dragging?.get(id));
  const resize = useBoardUI((s) => (s.resizing?.id === id ? s.resizing : null));
  const setEditing = useBoardUI((s) => s.setEditing);
  const ref = useRef<HTMLDivElement>(null);
  const report = useCallback(
    (h: number) => {
      if (inColumn) onHeight?.(id, h);
      else index.setHeight(id, h);
    },
    [index, id, inColumn, onHeight],
  );
  useElementHeight(ref, report);

  const dragging = !!drag;
  const x = drag?.x ?? c.x;
  const y = drag?.y ?? c.y;
  const w = resize?.w ?? c.w;
  const h = resize ? resize.h : c.h;
  const colors = cardColors(c.color, c.colorMode);
  const isTile = c.type === "board" || c.type === "alias";
  const isDrawing = c.type === "annotation";
  const tileTarget = c.childBoardId ?? c.targetBoardId;
  const editableText =
    c.type === "note" ||
    c.type === "clone" ||
    c.type === "annotation" ||
    c.type === "heading" ||
    c.type === "swatch" ||
    c.type === "sketch" ||
    c.type === "document";
  const positioned = !inColumn;

  return (
    <div
      ref={ref}
      data-card={id}
      data-color={c.color}
      className={`select-none ${positioned ? "absolute left-0 top-0" : "relative"} ${isTile || isDrawing ? "" : "rounded-[2px] shadow-[0_1px_4px_rgba(0,0,0,.5)]"} ${c.locked ? "opacity-90" : ""} ${isDrawing && !selected && !editing ? "pointer-events-none [&_path]:pointer-events-auto" : ""}`}
      style={{
        transform: positioned ? `translate(${x}px, ${y}px)` : undefined,
        width: inColumn ? "100%" : w,
        height: isTile ? "auto" : (h ?? "auto"),
        zIndex: dragging ? 50 : 1,
        background: isTile || isDrawing ? "transparent" : colors.bg,
        color: colors.fg,
        outline: selected ? `2px solid ${editing ? "#ffffff" : "#8aa9ff"}` : "none",
        outlineOffset: 2,
        contain: "layout style",
        cursor: editing ? "text" : "default",
        opacity: c.uploading ? 0.85 : undefined,
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        interactions.onCardPointerDown(e.nativeEvent, id, c.locked);
      }}
      onDoubleClick={(e) => {
        if (editableText && canEdit && !c.locked) {
          e.stopPropagation();
          setEditing(id);
        }
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const ui = useBoardUI.getState();
        if (!ui.selection.has(id)) ui.select([id]);
        const vp = (e.currentTarget.closest("[data-viewport]") as HTMLElement).getBoundingClientRect();
        ui.setContextMenu({ sx: e.clientX - vp.left, sy: e.clientY - vp.top, px: 0, py: 0, cardId: id });
      }}
    >
      {colors.strip ? <div className="h-[6px] w-full" style={{ background: colors.strip }} /> : null}
      {c.type === "note" || c.type === "clone" ? (
        <NoteBody
          id={id}
          fragment={noteFragment(ymap) ?? (ymap.get("body") as Y.XmlFragment)}
          fg={colors.fg}
        />
      ) : null}
      {(c.type === "note" || c.type === "clone") && c.syncedKey ? (
        <span className="absolute right-1 top-1 text-[11px] text-dim" title="synced">
          ⟲
        </span>
      ) : null}
      {c.type === "heading" ? <HeadingBody id={id} ymap={ymap} text={c.text ?? ""} /> : null}
      {c.type === "image" ? (
        <ImageBody id={id} fileId={c.fileId} uploading={c.uploading} aspect={c.aspect} w={w} name={c.name} />
      ) : null}
      {c.type === "swatch" ? <SwatchBody id={id} ymap={ymap} hex={c.hex ?? "#ffffff"} /> : null}
      {c.type === "link" ? (
        <LinkBody
          id={id}
          f={c as unknown as Parameters<typeof LinkBody>[0]["f"]}
          w={w}
          onSubmitUrl={(url) =>
            window.dispatchEvent(new CustomEvent("mn:resolve-link", { detail: { id, url } }))
          }
        />
      ) : null}
      {c.type === "file" ? (
        <FileBody
          id={id}
          fileId={c.fileId}
          name={c.name}
          mime={c.mime}
          size={c.size}
          uploading={c.uploading}
        />
      ) : null}
      {c.type === "video" || c.type === "audio" ? (
        <MediaBody id={id} kind={c.type} fileId={c.fileId} uploading={c.uploading} name={c.name} />
      ) : null}
      {c.type === "todo" ? <TodoBody id={id} ymap={ymap} /> : null}
      {c.type === "table" ? <TableBody id={id} ymap={ymap} /> : null}
      {c.type === "comment" ? (
        <CommentBody id={id} ymap={ymap} collapsed={(ymap.get("collapsed") as boolean) ?? false} />
      ) : null}
      {c.type === "sketch" || c.type === "annotation" ? (
        <SketchBody id={id} ymap={ymap} w={w} h={h ?? 240} bare={isDrawing} />
      ) : null}
      {c.type === "document" ? (
        <DocumentBody id={id} ymap={ymap} title={c.title ?? ""} fg={colors.fg} />
      ) : null}
      {c.type === "column" ? (
        <ColumnBody
          id={id}
          ymap={ymap}
          title={c.title ?? ""}
          collapsed={(ymap.get("collapsed") as boolean) ?? false}
        />
      ) : null}
      {isTile && tileTarget ? <BoardTileBody childBoardId={tileTarget} alias={c.type === "alias"} /> : null}
      {c.caption ? <div className="px-3 pb-2 text-[12px] text-muted">{c.caption}</div> : null}
      <CardChips
        labelIds={c.labels ?? []}
        reactions={ymap.get("reactions") as Y.Map<unknown> | undefined}
        cardId={id}
      />
      {selected && canEdit && !c.locked && !editing && !inColumn ? (
        <div
          className="absolute -bottom-1.5 -right-1.5 size-3 cursor-nwse-resize rounded-[2px] border border-bg bg-[#8aa9ff]"
          onPointerDown={(e) => {
            e.stopPropagation();
            interactions.onResizePointerDown(e.nativeEvent, id);
          }}
        />
      ) : null}
    </div>
  );
});
